import React, { useState } from 'react';
import { supabase } from '../../utils/supabase';
import { Eye, EyeOff, Bot } from 'lucide-react';

export default function Auth({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  const validate = () => {
    if (!form.email || !form.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!form.password || form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setError('');
    if (!validate()) return;
    setLoading(true);

    try {
      if (isLogin) {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: form.email,
          password: form.password,
        });
        if (error) throw error;
        onLogin(data.user);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email: form.email,
          password: form.password,
        });
        if (error) throw error;
        if (!data.user) throw new Error('Signup failed. Please try again.');
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="min-h-screen flex">

      {/* Left Side — Branding */}
      <div className="hidden lg:flex w-1/2 bg-primary flex-col items-center justify-center p-12 text-white">
        <div className="max-w-sm text-center">
          <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-6 backdrop-blur">
            <Bot size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4">TabBot</h1>
          <p className="text-white/80 text-lg mb-8">WhatsApp AI Chatbot for Saudi Restaurants</p>

          <div className="space-y-4 text-left">
            {[
              '🤖 AI replies in Arabic & English 24/7',
              '📊 Real-time orders dashboard',
              '👥 Customer CRM built automatically',
              '🍽️ Interactive WhatsApp menu',
              '🔔 Instant order notifications',
            ].map(feature => (
              <div key={feature} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                <span className="text-sm font-medium">{feature}</span>
              </div>
            ))}
          </div>

          <p className="text-white/60 text-sm mt-8">Serving restaurants in Jeddah 🇸🇦</p>
        </div>
      </div>

      {/* Right Side — Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-md">

          {/* Mobile Logo */}
          <div className="text-center mb-8 lg:hidden">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
              <Bot size={32} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800">TabBot</h1>
            <p className="text-gray-500 text-sm mt-1">WhatsApp AI for Restaurants</p>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-1">
              {isLogin ? 'Welcome back 👋' : 'Get started free'}
            </h2>
            <p className="text-sm text-gray-400 mb-6">
              {isLogin ? 'Login to manage your restaurant' : 'Create your TabBot account — first month free!'}
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  onKeyDown={handleKeyDown}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    onKeyDown={handleKeyDown}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
                  <span>⚠️</span> {error}
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-primary text-white py-3 rounded-xl font-medium hover:bg-secondary transition disabled:opacity-60 text-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Please wait...
                  </span>
                ) : isLogin ? 'Login →' : 'Create Free Account →'}
              </button>
            </div>

            {!isLogin && (
              <div className="mt-4 bg-green-50 border border-green-100 rounded-xl px-4 py-3">
                <p className="text-green-700 text-xs font-medium">🎁 First month completely FREE — no credit card needed!</p>
              </div>
            )}

            <p className="text-center text-sm text-gray-500 mt-6">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                onClick={() => { setIsLogin(!isLogin); setError(''); setForm({ email: '', password: '' }); }}
                className="text-primary font-medium ml-1 hover:underline"
              >
                {isLogin ? 'Sign up free' : 'Login'}
              </button>
            </p>
          </div>

          <p className="text-center text-xs text-gray-400 mt-4">
            🔒 Your data is encrypted and secure
          </p>
        </div>
      </div>
    </div>
  );
}